function asBufferSource(bytes: Uint8Array): BufferSource {
  return bytes as unknown as BufferSource;
}

const ab2str = (buf: ArrayBuffer | Uint8Array): string => {
  const uint8Array = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  const chunkSize = 8192;
  let result = "";
  for (let i = 0; i < uint8Array.length; i += chunkSize) {
    result += String.fromCharCode.apply(null, Array.from(uint8Array.subarray(i, i + chunkSize)));
  }
  return result;
};

const str2ab = (str: string): ArrayBuffer => {
  const buf = new ArrayBuffer(str.length);
  const bufView = new Uint8Array(buf);
  for (let i = 0, strLen = str.length; i < strLen; i++) {
    bufView[i] = str.charCodeAt(i);
  }
  return buf;
};

const getMessageForEncryption = (message: string): Uint8Array =>
  new TextEncoder().encode(message);

const getArrayBuffer = (message: string): ArrayBuffer =>
  str2ab(window.atob(message));

export const generateRsaKey = async (): Promise<CryptoKeyPair> =>
  window.crypto.subtle.generateKey(
    { name: "RSA-OAEP", modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]), hash: "SHA-256" },
    true,
    ["encrypt", "decrypt"],
  );

export const exportRsaCryptoKey = async (publicKey: CryptoKey): Promise<string> => {
  const exported = await window.crypto.subtle.exportKey("spki", publicKey);
  return window.btoa(ab2str(exported));
};

export const importRsaKey = async (publicKey: string): Promise<CryptoKey> =>
  window.crypto.subtle.importKey(
    "spki",
    getArrayBuffer(publicKey),
    { name: "RSA-OAEP", hash: "SHA-256" },
    true,
    ["encrypt"],
  );

export const rsaEncryptMessage = async (publicKey: CryptoKey, message: string): Promise<string> => {
  const encoded = getMessageForEncryption(message);
  const cipher = await window.crypto.subtle.encrypt({ name: "RSA-OAEP" }, publicKey, asBufferSource(encoded));
  return window.btoa(ab2str(cipher));
};

export const rsaDecryptMessage = async (privateKey: CryptoKey, message: string): Promise<string> => {
  const decrypted = await window.crypto.subtle.decrypt({ name: "RSA-OAEP" }, privateKey, getArrayBuffer(message));
  return new TextDecoder().decode(decrypted);
};

export const generateAesKey = async (): Promise<CryptoKey> =>
  window.crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);

export const exportAesCryptoKey = async (key: CryptoKey): Promise<string> => {
  const exported = await window.crypto.subtle.exportKey("raw", key);
  return window.btoa(ab2str(new Uint8Array(exported)));
};

export const importAesKey = async (key: string): Promise<CryptoKey> =>
  window.crypto.subtle.importKey("raw", getArrayBuffer(key), "AES-GCM", true, ["encrypt", "decrypt"]);

export const aesEncryptMessage = async (
  key: CryptoKey,
  message: string,
): Promise<{ aesCipheredText: string; ivBase64: string }> => {
  const encoded = getMessageForEncryption(message);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const ivBase64 = window.btoa(ab2str(iv));
  const cipher = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv: asBufferSource(iv) },
    key,
    asBufferSource(encoded),
  );
  return { aesCipheredText: window.btoa(ab2str(cipher)), ivBase64 };
};

export const aesDecryptMessage = async (
  key: CryptoKey,
  ivBase64: string,
  message: string,
): Promise<string> => {
  const decrypted = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv: getArrayBuffer(ivBase64) },
    key,
    getArrayBuffer(message),
  );
  return new TextDecoder().decode(decrypted);
};
